import { useContext, createContext, useState, useEffect } from "react";
import { baseURL } from "../lib";
import { useAuth } from "./AuthContext";
import axios from 'axios'

const RestaurantContext = createContext()

const RestaurantProvider = ({ children }) => {
    const { auth, setAuth, toast, restaurantId, setRestaurantId, headers } = useAuth();

    const [selectedRestaurantId, setSelectedRestaurantId] = useState(null)

    const getAllRestaurants = async (currentPage, rowsPerPage, sortField, sortOrder, filter) => {
        try {
            const { data } = await axios.get(`${baseURL}/restaurant/list?sortField=${sortField}&sortOrder=${sortOrder}&page=${currentPage}&limit=${rowsPerPage}&filter=${filter !== '' ? filter : null}`, { headers })

            if (data.error === false) {
                return data
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    const getUserRestaurants = async (currentPage, rowsPerPage, sortField, sortOrder, filter) => {
        try {
            let { data } = await axios.get(`${baseURL}/user/restaurants?sortField=${sortField}&sortOrder=${sortOrder}&page=${currentPage}&limit=${rowsPerPage}&filter=${filter !== '' ? filter : null}`, { headers })
            if (data.error === false) {
                return data
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'User', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    const getSingleRestaurant = async (id) => {
        try {
            let { data } = await axios.get(`${baseURL}/restaurant/single-restaurant/${id}`, { headers })
            if (data.error === false) {
                return data.data
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    const updateLocalAuthData = (newPermissions) => {
        const localStorageData = localStorage.getItem('auth');
        if (!localStorageData) return;

        try {
            const parsedData = JSON.parse(localStorageData);
            const currentPermissions = parsedData.user?.permissions || [];

            const updatedPermissions = [...currentPermissions, newPermissions];

            const updatedAuthData = {
                ...parsedData,
                user: {
                    ...parsedData.user,
                    permissions: updatedPermissions,
                },
            };

            localStorage.setItem('auth', JSON.stringify(updatedAuthData));
            setAuth(updatedAuthData);
        } catch (error) {
            console.error('Error updating auth data:', error);
        }
    };

    const createRestaurant = async (printerDetail) => {
        try {
            const { data } = await axios.post(`${baseURL}/restaurant/create`, printerDetail, { headers });
            if (data.error === false) {
                if (!auth?.user?.is_super_admin) {
                    updateLocalAuthData(data?.data)
                }

                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Restaurant', detail: data.message, life: 3000 })
                }, 500);

                return data;
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: errors[0].msg, life: 3000 })
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    const updateRestaurant = async (id, printerDetail) => {
        try {
            const { data } = await axios.put(`${baseURL}/restaurant/update/${id}`, printerDetail, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Restaurant', detail: data.message, life: 3000 })
                }, 500);
                return data;
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: errors[0].msg, life: 3000 })
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    const updateRestaurantPin = async (id, newPin) => {
        try {
            const { data } = await axios.put(`${baseURL}/restaurant/update/pin/${id}`, { pin: newPin }, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Restaurant', detail: data.message, life: 3000 })
                }, 500);
                return data
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: errors[0].msg, life: 3000 })
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    const deleteRestaurant = async (id) => {
        try {
            const { data } = await axios.delete(`${baseURL}/restaurant/delete/${id}`, { headers });
            if (data.error === false) {
                const response = await getAllRestaurants(1, 50, 'id', -1, '');
                if (response.data.length > 0) {
                    setSelectedRestaurantId({ value: response.data[0].id, label: response.data[0].name });
                    setRestaurantId(response.data[0].id)
                }
                toast.current.show({ severity: 'success', summary: 'Restaurant', detail: data.message, life: 3000 })
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: data.message, life: 3000 })
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    const cloneProducts = async (restaurant_id, products) => {
        try {
            const { data } = await axios.post(`${baseURL}/restaurant/clone/products`, { restaurant_id, products }, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Restaurant', detail: data.message, life: 3000 })
                }, 500);
                return data;
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: errors[0].msg, life: 3000 })
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Restaurant', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    useEffect(() => {
        const setDefaultRestaurantId = async () => {
            if (!restaurantId && auth.user) {
                if (auth.user) {
                    const response = await getUserRestaurants(1, 50, 'id', -1, '');
                    if (response.data.length > 0) {
                        setSelectedRestaurantId({ value: response.data[0].id, label: response.data[0].name });
                        setRestaurantId(response.data[0].id)
                    }
                } else {
                    setRestaurantId(auth.user.restaurantIds[0])
                }
            }
        }
        setDefaultRestaurantId()
    }, [restaurantId, headers, auth.token])

    return (
        <RestaurantContext.Provider value={{ getAllRestaurants, getUserRestaurants, getSingleRestaurant, createRestaurant, updateRestaurant, deleteRestaurant, selectedRestaurantId, setSelectedRestaurantId, updateRestaurantPin, cloneProducts }}>
            {children}
        </RestaurantContext.Provider>
    )
}

const useRestaurant = () => useContext(RestaurantContext)

export { useRestaurant, RestaurantProvider }